/**
 * The one place the web app talks to the API. Every call carries a Firebase ID
 * token; the server verifies it, resolves it to a Principal, and decides through
 * `authorize()`.
 *
 * Nothing here interprets permissions. The UI may hide a control the user cannot
 * use, but hiding is a courtesy — the server is what refuses, and the two must
 * not drift into a client-side check that looks like enforcement.
 */

import type {
  CaseStatus,
  ClientReportCategory,
  ClientReportStatus,
  DocumentKind,
  IntakeAdvisory,
  IntakeGap,
  MeetingMode,
  MeetingParty,
  MeetingStatus,
} from "@titleverify/shared";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /**
     * True only when THIS API produced the response. False means something in
     * front of it answered — Cloud Run declining to invoke the service, a proxy,
     * an outage page — or that nothing answered at all. A 401 from each means
     * completely different things, and only the first one licenses saying
     * anything about the caller's account.
     */
    readonly fromApi: boolean = false,
    /**
     * True only when the server wrote a `message` — a SENTENCE aimed at the
     * caller, explaining a rule they have to satisfy ("This case must be routed
     * before an attorney can be assigned").
     *
     * False means `message` here is a fallback: the bare machine token from the
     * `error` key ("forbidden", "not_found", "internal_error", "invalid_request")
     * or a constructed string. Those are for logs and for `instanceof` branches,
     * not for a reader, and printing one as though it were an explanation is how
     * "internal_error" ends up on a client's screen as the platform's account of
     * what happened to their case.
     *
     * This is recorded rather than guessed at from the string, because the two
     * are genuinely indistinguishable after the fact — a one-word server message
     * and a one-word error code look identical.
     */
    readonly serverExplained: boolean = false,
    /**
     * The server's machine-readable discriminator, when it sent one.
     *
     * Only `no_account` exists today, and only on a 401. It separates "your
     * token is real but there is no TitleVerify account behind it" — the one
     * 401 a client can act on — from "your session is bad or expired", which
     * previously looked identical from here. The app showed its account-setup
     * screen for both, so a client whose session had merely expired was told
     * their account did not exist, on a screen whose only button then failed.
     */
    readonly code: string | null = null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * The status on an ApiError when THERE WAS NO RESPONSE — `fetch` itself rejected,
 * so nothing ever came back to have a status.
 *
 * Zero is not an HTTP status and is not treated as one anywhere: `apiErrorText`
 * branches on it before it can be printed, precisely so no screen quotes a
 * status the wire never produced. It sits alongside `fromApi: false`, which is
 * the stronger statement — nothing this app can see answered the request, so
 * nothing may be said about the caller's account or permissions.
 */
export const NO_RESPONSE = 0;

/**
 * `fetch` itself failing is a DIFFERENT EVENT from the API refusing, and until
 * now the two were not told apart: nothing caught the rejection, so the browser's
 * own wording reached the user through `apiErrorText` — "Failed to fetch",
 * "NetworkError when attempting to fetch resource". Those name an implementation
 * the reader has no access to and describe no action they can take, and on a
 * platform whose whole promise is not stating things it did not establish, a
 * user cannot be left to guess whether "Failed to fetch" meant their case was
 * rejected.
 *
 * Offline, DNS, TLS, CORS, an aborted request — every one of them means the same
 * thing to the person reading the screen, and it is the one thing worth saying:
 * the request never reached the service, so the state of their case is exactly
 * what it was.
 */
async function requestApi(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch {
    throw new ApiError(
      NO_RESPONSE,
      "The request did not reach the TitleVerify service.",
      false,
    );
  }
}

/**
 * Reading a SUCCESSFUL response's body, which can still fail.
 *
 * `throwForResponse` already guards the failure path; this guards the 2xx one,
 * where `response.json()` on a body that is not JSON throws the parser's own
 * string — "Unexpected token '<'…", the signature of an HTML page served where
 * JSON was expected (a hosting rewrite answering with index.html, an outage page
 * with a 200). That string is meaningless to a user and it was reaching them.
 *
 * `fromApi` stays FALSE, and that is the honest reading rather than a
 * convenience: this API's every response is JSON (http/errors.ts), so a body
 * that will not parse is evidence the answer did not come from it. The flag
 * claims our API answered, and here we cannot establish that.
 */
async function readJson<T>(response: Response): Promise<T> {
  try {
    return (await response.json()) as T;
  } catch {
    throw new ApiError(
      response.status,
      `The reply to this request was not something this app could read (${response.status}).`,
      false,
    );
  }
}

type Body = Record<string, unknown>;

export async function apiFetch<T>(
  path: string,
  options: { token: string | null; method?: string; body?: Body } = { token: null },
): Promise<T> {
  const response = await requestApi(`/api${path}`, {
    method: options.method ?? "GET",
    headers: {
      ...(options.token ? { Authorization: `Bearer ${options.token}` } : {}),
      ...(options.body ? { "Content-Type": "application/json" } : {}),
    },
    ...(options.body ? { body: JSON.stringify(options.body) } : {}),
  });

  if (!response.ok) await throwForResponse(response);

  return await readJson<T>(response);
}

/**
 * The ONE place a failed response becomes an ApiError.
 *
 * Extracted so that every transport into this API — the JSON `apiFetch` and the
 * raw-body `apiUpload` below — produces identically-classified errors. A second
 * hand-rolled error path would be a second place for the `fromApi` distinction
 * to be forgotten, and that distinction is the difference between telling a user
 * something true about their account and telling them something invented.
 *
 * Surface the server's reason where it gave one — those messages explain a
 * rule the caller has to satisfy. Never invent a friendlier one.
 *
 * `fromApi` records whether THIS API answered at all, and it is load-bearing
 * rather than cosmetic. Our error shape is always JSON with an `error` key
 * (http/errors.ts). Anything else returning a status — Cloud Run refusing to
 * invoke the service, a proxy, an outage page — produces HTML or nothing.
 *
 * Without the distinction a caller cannot tell "the API says you have no
 * account" from "the request never reached the API", and both arrive as 401.
 * That is not hypothetical: staging served a Cloud Run authorization 401 and
 * the app confidently reported it as "your account is not set up yet",
 * stating a specific wrong cause to a user who could do nothing about it.
 *
 * `error` is read as well as `message` because auth failures carry only
 * `error` ({"error":"unauthenticated"}), so reading `message` alone threw
 * away the one word the server did say.
 */
async function throwForResponse(response: Response): Promise<never> {
  const detail = (await response.json().catch(() => null)) as
    | { error?: string; message?: string; code?: string }
    | null;
  const fromApi = detail !== null && typeof detail.error === "string";
  const serverExplained = typeof detail?.message === "string" && detail.message !== "";
  throw new ApiError(
    response.status,
    detail?.message ?? detail?.error ?? `Request failed (${response.status})`,
    fromApi,
    serverExplained,
    typeof detail?.code === "string" ? detail.code : null,
  );
}

/**
 * The document upload, which cannot go through `apiFetch`.
 *
 * `apiFetch` JSON-stringifies its body and declares application/json. The
 * documents route takes the file as the RAW REQUEST BODY with the file's own
 * content type and no envelope at all: no multipart, no field names, no
 * filename, no `kind` (apps/api/src/documents/routes.ts — the parser is
 * `express.raw` over the four accepted types).
 *
 * Two things are absent from this signature on purpose, and neither is an
 * oversight to be fixed later:
 *
 *  - NO FILENAME. The API has nowhere to put one. Client filenames routinely
 *    carry a person's name and would reach storage keys, logs and metrics, none
 *    of which are erasable.
 *  - NO KIND. It is derived from the caller's role on the server. A parameter
 *    here would be silently ignored and would imply a control that does not
 *    exist.
 *
 * There is no upload progress, because `fetch` exposes none. Callers may report
 * which file they are on; they may not report a percentage they do not have.
 */
export async function apiUpload<T>(
  path: string,
  options: { token: string | null; file: Blob },
): Promise<T> {
  const response = await requestApi(`/api${path}`, {
    method: "POST",
    headers: {
      ...(options.token ? { Authorization: `Bearer ${options.token}` } : {}),
      // The content type IS the request's whole description of the payload.
      "Content-Type": options.file.type,
    },
    body: options.file,
  });

  if (!response.ok) await throwForResponse(response);

  return await readJson<T>(response);
}

/**
 * Turns a thrown error into a sentence, without claiming more than the response
 * licensed. Callers pass what they were trying to do; this supplies the why.
 *
 * The status-by-status handling is not decoration. On the documents surface:
 *
 *  - 400 carries a real, specific, caller-facing `message` written by the
 *    server ("The file is empty.", "This document was deleted when its
 *    retention period ended…"). It is rendered VERBATIM. Softening it would
 *    throw away the only useful thing in the response.
 *  - 403 and 404 carry NO message — `ApiError.message` is then the bare token
 *    "forbidden" or "not_found", which is not a sentence and is not for a user.
 *    The server withholds the reason deliberately, so the UI must not guess at
 *    one either. This is a PLATFORM-WIDE rule, not a documents one: http/errors.ts
 *    strips the reason from every 403 because naming the rule would confirm that
 *    someone else's case exists. (The 403 sentence below used to say "on
 *    documents", which was true of the only caller at the time and became a
 *    false narrowing the moment the case surfaces started using this. The
 *    alternative — a second hand-rolled error path per surface — is exactly how
 *    the `fromApi` distinction gets forgotten somewhere.)
 *  - `fromApi === false` means something in front of the API answered, or that
 *    nothing answered at all. Saying anything about permissions or accounts
 *    there would be inventing a cause.
 *  - `status === NO_RESPONSE` is the second of those two and gets its own
 *    sentence, because the first one quotes a status and there is none to quote.
 *    It is also the more actionable of the two for the reader: a request that
 *    never left is usually their connection, whereas something-else-answered is
 *    an operator's problem.
 */
export function apiErrorText(error: unknown, whatFailed: string): string {
  if (error instanceof ApiError) {
    if (!error.fromApi) {
      if (error.status === NO_RESPONSE) {
        return (
          `${whatFailed} The request never reached the TitleVerify service — nothing answered it ` +
          `at all, which usually means this device is offline or the service is unreachable from ` +
          `here. Nothing was sent, so nothing has changed, and this says nothing about your ` +
          `account or your permissions. Check the connection and try again.`
        );
      }
      if (error.status >= 200 && error.status < 300) {
        /*
         * A SUCCESS STATUS THAT IS NOT OUR ANSWER. The body would not parse, so
         * it did not come from this API — an index.html served by a hosting
         * rewrite for a path that no longer exists is the way this happens.
         *
         * It needs its own sentence because the one below says "did not reach"
         * and then quotes the status, and "did not reach the service (200)" is a
         * sentence that argues with itself. Both are the same fact — something
         * other than our API answered — but only this one can say the request
         * looked successful, which is what makes it confusing to be sent.
         */
        return (
          `${whatFailed} Something answered the request, but not the TitleVerify service — the ` +
          `reply was not in a form this app can read. It says nothing about your account or ` +
          `your permissions, and nothing here should be taken as an answer about your case. ` +
          `If it keeps happening it needs an operator, not you.`
        );
      }
      return (
        `${whatFailed} The request did not reach the TitleVerify service (${error.status}), so ` +
        `this says nothing about your account or your permissions — we cannot tell from here ` +
        `what answered. Trying again shortly is the right move.`
      );
    }
    if (error.status === 401) {
      return `${whatFailed} The service does not recognise you as signed in. Sign in again.`;
    }
    if (error.status === 403) {
      return (
        `${whatFailed} The service refused it and did not say why — it does not explain refusals, ` +
        `and this page is not going to guess at a reason.`
      );
    }
    if (error.status === 404) {
      return `${whatFailed} The service has no record under that reference.`;
    }

    // The server wrote a sentence for the caller. It is the most useful thing in
    // the response and it goes through untouched — this is the 400 and 409 case,
    // where the message IS the rule that has to be satisfied.
    if (error.serverExplained) return `${whatFailed} ${error.message}`;

    if (error.status >= 500) {
      /*
       * OUR SERVICE FAILED ON ITS SIDE. Every 5xx this API produces is
       * `{"error":"internal_error"}` with no message (http/errors.ts), so the
       * previous line here printed the token `internal_error` at the user as if
       * it were an account of what happened.
       *
       * What is said instead is only what a 5xx actually establishes: the
       * failure is ours. It notably does NOT say the request had no effect —
       * that is unknown from here, a 500 can be thrown after a write, and
       * telling someone their submission did not go through when it may have is
       * the same fabrication in the reassuring direction.
       */
      return (
        `${whatFailed} The TitleVerify service failed while handling it (${error.status}). That is a ` +
        `fault on our side, not something about your account or anything you did. This screen ` +
        `cannot tell whether the request took effect before it failed, so check before repeating ` +
        `it — and if it keeps happening it needs an operator.`
      );
    }

    /*
     * A STATUS WITH NO WORDING. `error.message` here is the bare machine token
     * from the `error` key, which is not a sentence and was not written for a
     * reader. The code is quoted as a code, because an operator can act on it and
     * the user can repeat it — but it is not dressed up as an explanation, and no
     * cause is invented to fill the gap.
     */
    return (
      `${whatFailed} The service refused it (${error.status}) without wording this app can show ` +
      `you. The code it gave is "${error.message}". This page will not guess at what it meant.`
    );
  }

  /*
   * NOT AN ApiError, so it did not come from a request at all — every transport
   * in this file now throws ApiError, including the two failure modes that used
   * to arrive here as the browser's own prose. What is left is this app failing
   * BEFORE the request: getting a token, most likely.
   *
   * The message is still shown, because withholding it leaves a user and an
   * operator with nothing to go on — but it is framed as this app's own failure
   * so it cannot be misread as the service's answer about their case. The cast
   * that used to be here would have printed "undefined" for anything thrown
   * that was not an Error.
   */
  const detail = error instanceof Error ? error.message : String(error);
  return (
    `${whatFailed} This app failed before it could ask the TitleVerify service, so the request ` +
    `was never made and nothing has changed. It reported: ${detail}`
  );
}

export type ProfessionalProfile = {
  id: string;
  userId: string;
  type: "attorney" | "notary" | "both";
  enrolmentNo: string | null;
  notaryRegistrationNo: string | null;
  verificationStatus: "unverified" | "pending" | "verified" | "rejected" | "revoked";
  verifiedAt: string | null;
  createdAt: string;
  assignable: boolean;
};

/**
 * A case as every role's status view returns it (workflow/routes.ts `present`).
 *
 * `registryDistrict` and `landSystem` are null until the platform has actually
 * resolved them, and every screen must render that null as unknown rather than
 * as a blank that reads like an answer (CLAUDE.md non-negotiable 1).
 */
export type Case = {
  id: string;
  clientId: string;
  status: CaseStatus;
  registryDistrict: string | null;
  landSystem: "deed" | "title" | null;
  needsInfoGaps: unknown;
  assignedProfessionalId: string | null;
  /**
   * Which land this is, as the CLIENT described it — null for any caller the
   * server does not scope it to (the owning client and the assigned attorney,
   * nobody else). Optional on the type so a response predating this field, or a
   * role without it, is a normal absence rather than a crash.
   *
   * NOT a description of the land as the registry holds it. Establishing that is
   * what the search does, which is why the client's surface labels this "the
   * land you described".
   */
  parcel?: {
    district: string | null;
    village: string | null;
    address: string | null;
    surveyPlanNo: string | null;
    lotNo: string | null;
  } | null;
  createdAt: string;
  updatedAt: string;
};

/**
 * What `POST /admin/cases/:id/route` answers with (workflow/routes.ts).
 *
 * The case, plus the two fields that say how the attempt went. It extends `Case`
 * rather than redeclaring it, so a field added to the case payload cannot be
 * silently missing here.
 *
 * `routed: false` WITH `registryDistrict: null` IS A NORMAL, EXPECTED OUTCOME,
 * not an error. `registry_offices` is empty until the Registrar General's dataset
 * is seeded with provenance, so today it is the usual answer. The case moves to
 * (or stays at) VALIDATING — the ops queue — and NOTHING is guessed.
 *
 * WHAT THIS DOES NOT TELL YOU, AND MUST NOT BE MADE TO: routing can fail to
 * resolve for three different reasons — no active office matches the parcel's
 * district, several do (ambiguous, which real Colombo data will produce), or the
 * parcel's district is blank. The response is identical in all three. Any UI copy
 * naming a cause would be stating a fact the platform did not derive.
 *
 * There is also no operator input anywhere in this call. The route endpoint reads
 * NO BODY AT ALL: the office is resolved from the PARCEL, and there is deliberately
 * no field through which anyone could name one (see routing.ts).
 */
export type RouteResult = Case & { routed: boolean; registryDistrict: string | null };

/**
 * What `POST /cases` AND `PATCH /cases/:id/parcel` answer with — the same
 * `present()` in intake/routes.ts serves both, so one type covers both.
 *
 * BOTH STATUSES ARE SUCCESSES. `SUBMITTED` means the parcel can be looked for at
 * a registry; `NEEDS_INFO` means the case was created and saved and is waiting on
 * one more detail. A screen that renders the second as a failure would be
 * throwing away a case the server kept, and telling the client their work was
 * lost when it was not.
 *
 * Note what is absent: no `registryDistrict`, no resolved `landSystem`. Neither
 * is worked out at submission — routing happens later, against the registry
 * table — so there is nothing here for a screen to state, and nothing to be
 * tempted into inferring (CLAUDE.md non-negotiable 1). The same is true of the
 * PATCH: a supplement that comes back SUBMITTED means the parcel can be LOOKED
 * FOR at a registry. It does not mean it was found, the district confirmed, or
 * the registration system established.
 *
 * ON THE PATCH SPECIFICALLY: `needsInfo` is a FRESH assessment of the whole
 * merged parcel, not the previous list minus what was answered. Answering one
 * question can surface a different one, so a screen must re-render from what
 * came back and must not congratulate on a 200.
 *
 * Note the key is `caseId`, not `id` — this shape is NOT a `Case`.
 */
export type IntakeSubmission = {
  caseId: string;
  status: CaseStatus;
  /** The blocking gaps, each with the shared GAP_GUIDANCE wording the server looked up. */
  needsInfo: { code: IntakeGap; message: string }[];
  /** Worth having, never worth refusing over. Codes only — the server sends no copy for these. */
  advisories: IntakeAdvisory[];
};

/**
 * The whole of what `POST /consents/revoke` answers with. One number.
 *
 * `revoked` is how many consent rows were LIVE and matched the (user, scope,
 * purpose, channel) the caller named, and have now been stamped withdrawn.
 *
 * ZERO IS NOT AN ERROR AND NOT A SUCCESS, and it is the trap on this endpoint.
 * It means there was nothing live to withdraw — never granted, or already
 * withdrawn. Rendering "Withdrawn." over a no-op tells the client something
 * happened that did not; rendering `role="alert"` tells them something broke
 * that did not. It is a fact about our records and reads as one.
 *
 * Zero also does NOT mean "that case is not yours". There is no 403 and no 404
 * on this route: it is self-scoped to the caller's own rows, so a caseId
 * belonging to somebody else simply matches nothing. No copy may read a
 * zero as an answer about ownership.
 *
 * WHAT IS ABSENT: any way to ask what is currently consented. `hasConsentForCase`
 * exists in the API but is exported only for its tests and no route calls it, so
 * this count — which arrives only AFTER the act — is the sole consent signal the
 * browser ever gets. A preferences panel with toggles cannot honestly be built
 * on it; one-way withdrawal can.
 */
export type ConsentRevocation = { revoked: number };

/**
 * A report as the CLIENT WHO FILED IT sees it (reports/routes.ts
 * `presentForReporter`). Note what is not here: no `aboutUserId`, no
 * `reviewedBy`. The server withholds both, and this type records that rather
 * than papering over it with optional fields a screen might try to read.
 *
 * `statusMeaning` comes from the server, from CLIENT_REPORT_STATUS_MEANINGS. It
 * is never re-derived here. The whole risk in this feature is a screen printing
 * the bare word "upheld" and letting the reader supply the ordinary meaning of
 * it, which is a finding of misconduct we have not made and may not make.
 */
export type MyClientReport = {
  id: string;
  caseId: string;
  category: ClientReportCategory;
  detail: string | null;
  status: ClientReportStatus;
  statusMeaning: string;
  reviewedAt: string | null;
  createdAt: string;
};

/** The internal ops view (`presentForAdmin`). Carries the parties, because triaging is the admin's job. */
export type AdminClientReport = MyClientReport & {
  reportedByUserId: string;
  aboutUserId: string;
  reviewedBy: string | null;
  reviewNote: string | null;
};

/**
 * The roster tallies. All three counts arrive together on purpose — see
 * `reportCountsByProfessional` in the API. A bare upheld count beside a name,
 * with unreviewed reports invisible, invites a conclusion the data does not
 * support.
 */
export type ReportCounts = {
  aboutUserId: string;
  upheldCount: number;
  openCount: number;
  dismissedCount: number;
};

export type ReportCountsResponse = {
  counts: ReportCounts[];
  meanings: Record<ClientReportStatus, string>;
  /** The server's own caveat about what these numbers are not. Rendered, not paraphrased. */
  note: string;
};

/**
 * A document in the platform's custody, exactly as the API presents it
 * (documents/routes.ts `present`).
 *
 * WHAT IS NOT HERE MATTERS MORE THAN WHAT IS. Every absence below is a decision,
 * not a gap to be filled in by a screen:
 *
 *  - NO FILENAME, on the response or on the row. It is never sent, never
 *    stored, never logged and is not part of the storage key. A UI that keeps
 *    the local File.name and shows it in a list is reintroducing exactly the
 *    personal data the API strips. Documents are identified here by what they
 *    are, when they arrived, and their digest.
 *  - NO `uploadedBy`. The column exists; it is not presented. `kind` is the only
 *    thing the client is told about who added a document, and it is a statement
 *    about a ROLE (see DOCUMENT_KIND_LABELS). No surface may name a person.
 *  - NO `storageKey`. An internal address, and a key that leaks into a client is
 *    one more copy of a capability that cannot be revoked.
 *
 * `retentionExpiresAt` is exposed ON PURPOSE: the deletion clock is a promise
 * made to the client, so they can see it rather than discover it.
 *
 * `deleted` is true only when the stored object HAS BEEN destroyed. It is never
 * "scheduled for deletion" and must never be inferred from `retentionExpiresAt`
 * having passed — the flag is the only thing that licenses saying a document is
 * gone.
 */
export type CaseDocument = {
  id: string;
  caseId: string;
  /** Derived server-side from the uploader's role. Never an input, never a choice. */
  kind: DocumentKind;
  /** 64 lowercase hex. Computed from the bytes the server wrote, never accepted from the caller. */
  sha256: string;
  contentType: string | null;
  sizeBytes: number | null;
  retentionExpiresAt: string;
  deleted: boolean;
  createdAt: string;
};

/** GET /cases/:id/documents. Unordered — the API applies no `orderBy`. */
export type CaseDocumentList = { documents: CaseDocument[] };

/**
 * What GET /documents/:id/url answers with.
 *
 * Minted fresh per request and served `Cache-Control: no-store`. There is no
 * non-expiring variant and no stored URL anywhere in the system. `expiresInSeconds`
 * is the server's own number and is rendered rather than restated as a constant,
 * so a change to SIGNED_URL_TTL_SECONDS cannot leave this app quoting the old one.
 *
 * NEVER FETCH THIS WITH useQuery. A signed URL sitting in a shared cache is the
 * public object the whole storage design forbids, arrived at by another route.
 */
export type SignedDocumentUrl = { url: string; expiresInSeconds: number };

/**
 * A meeting between the client and the attorney ALREADY on their case, exactly
 * as the API presents it (meetings/routes.ts `present`).
 *
 * `scheduledAt` and `createdAt` ARE STRINGS, not Dates. `present()` types them
 * `Date` in the API's own source, but they cross the wire through `res.json()`
 * and arrive as ISO strings. Typing them `Date` here compiles perfectly and then
 * throws at runtime on the first `.toLocaleString()`.
 *
 * WHAT IS ABSENT, AND WHAT EACH ABSENCE COSTS THE UI:
 *
 *  - NO `requestedByUserId`. The column exists and it is the server's source of
 *    truth for both "only the OTHER party accepts" and "is this request stranded
 *    by a reassignment". It is not presented, so THE BROWSER CANNOT COMPUTE
 *    EITHER. A screen must not try: it renders the control, and the server
 *    refuses with a sentence the screen shows verbatim.
 *  - NO `updatedAt`, so there is no way to say when a status last moved. Only
 *    `createdAt` — when it was proposed — is knowable here.
 *  - NO NAME, EMAIL OR ANY IDENTIFIER OF THE OTHER PARTICIPANT, anywhere on this
 *    payload. `requestedBy` is a party label ("client" | "professional") and is
 *    the whole of what the wire says about who. That is not an oversight to be
 *    filled in from another endpoint — a meeting surface that identifies an
 *    attorney is the touting shape COMPLIANCE Constraint 1 forbids.
 *
 * `scheduledAt` is null while a meeting is merely `requested`, and null there
 * means NO TIME HAS BEEN AGREED — never "to be confirmed", and never a blank
 * cell that reads like an answer. Same for `location` / `meetingLink`.
 */
export type Meeting = {
  id: string;
  caseId: string;
  mode: MeetingMode;
  status: MeetingStatus;
  /** Which PARTY proposed it. Not a user id — see the note above. */
  requestedBy: MeetingParty;
  scheduledAt: string | null;
  location: string | null;
  meetingLink: string | null;
  createdAt: string;
};

/**
 * GET /cases/:id/meetings — wrapped in a key. Ordered newest-proposed first by
 * the server (`desc(createdAt)`).
 *
 * NOTE THE ASYMMETRY: every POST on this surface (request, accept, decline,
 * cancel, complete) answers with the BARE meeting object, unwrapped. One shared
 * mutation helper reading `.meetings` off a POST response gets undefined.
 *
 * An empty array is the honest normal state of an assigned case nobody has
 * proposed a meeting on yet — not an error, and not something to fill with a
 * placeholder meeting.
 */
export type MeetingList = { meetings: Meeting[] };

/**
 * A panel seat as the ADMIN screen reads it (panel/routes.ts `present`).
 *
 * INTERNAL OPS ONLY. There is no client-facing route that returns this, and there
 * must never be one: a surface a client can reach that lists, names or compares
 * professionals is the directory COMPLIANCE Constraint 1 (Rule 39) forbids. A
 * client learns the name of the attorney on their own case, from their own case.
 *
 * WHAT IS ABSENT AND WHY EACH ABSENCE IS A DECISION:
 *
 *  - NO POSITION, ORDER, SHARE, CAPACITY OR CASE COUNT. Nothing computes one, so
 *    nothing may display one. The rotation's order is derived per case at the
 *    moment of assignment, which is also why this payload cannot answer "who is
 *    next" — the final tiebreak is a per-case digest.
 *  - NO FEE, PRICE, RATE OR PAYOUT of any kind. D1 is open (CLAUDE.md
 *    non-negotiable 3).
 *  - NO NAME OR CONTACT DETAIL. `enrolmentNo` is the identifier an admin actually
 *    verified against a named source, and it is the same one the roster leads
 *    with — the API holds no name to send.
 *
 * `lastAssignedAt` IS NULL WHEN THEY HAVE NEVER BEEN ASSIGNED AT THIS REGISTRY,
 * and must render as that rather than as a blank or a zero. It is a date, not a
 * score: it says when they last received work here and nothing about volume,
 * quality or standing.
 */
export type PanelMembership = {
  id: string;
  registryOfficeCode: string;
  professionalUserId: string;
  enrolmentNo: string | null;
  verificationStatus: "unverified" | "pending" | "verified" | "rejected" | "revoked";
  joinedAt: string;
  leftAt: string | null;
  leftReason: string | null;
  pausedAt: string | null;
  pausedReason: string | null;
  source: string;
  sourcedAt: string;
  sourcedBy: string;
  lastAssignedAt: string | null;
};

export type RegistryOffice = {
  code: string;
  name: string;
  district: string | null;
  province: string | null;
  active: boolean;
  source: string;
  sourcedAt: string;
  sourcedBy: string | null;
  createdAt: string;
  updatedAt: string;
};
